import React, { useContext, useRef, useState, useEffect } from 'react';
import Button from "../../components/atoms/Button";
import ButtonGroup from "../../components/molecules/ButtonGroup";
import InputGroup from "../../components/molecules/InputGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateRight } from "@fortawesome/free-solid-svg-icons";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from 'moment-timezone';

const EventHeaderNav = ({
    locationData,
    loading,
    isToday,
    onRefresh,
    selectedDate,
    setSelectedDate,
    minDate,
    maxDate,
    enabledDates,
    showCalendar,
    setShowCalendar
}) => {
    const calendarRef = useRef(null);

    // Utility function to safely convert to Date object
    const toDate = (date) => {
        if (!date) return null;
        if (date instanceof Date) return date;
        
        // If it's a string in YYYY-MM-DD format and we have locationData with timezone
        if (typeof date === 'string' && locationData?.timeZone) {
            // Parse the date string as if it's in the location's timezone
            return moment.tz(date, 'YYYY-MM-DD', locationData.timeZone).toDate();
        }
        
        return new Date(date);
    };

    // Convert props to Date objects
    const minDateObj = toDate(minDate);
    const maxDateObj = toDate(maxDate);
    const enabledDatesArray = enabledDates?.map(toDate) || [];

    const handleCalendarToggle = () => {
        setShowCalendar(!showCalendar);
    };

    const handleDateSelect = (date) => {
        let clampedDate = date;
    
        if (minDateObj && date < minDateObj) {
            clampedDate = minDateObj;
        } else if (maxDateObj && date > maxDateObj) {
            clampedDate = maxDateObj;
        }
        setSelectedDate(date);
        setShowCalendar(false);
    };

    // Function to disable dates that are not in enabledDates array
    const tileDisabled = ({ date, view }) => {
        // Only apply to month view
        if (view !== 'month') return false;
        
        // If no enabledDates provided, don't disable any dates within min/max range
        if (!enabledDatesArray || enabledDatesArray.length === 0) return false;
        
        // Check if the date is in the enabledDates array
        const dateString = moment(date).format('YYYY-MM-DD');
        return !enabledDatesArray.some(enabledDate => 
            moment(enabledDate).format('YYYY-MM-DD') === dateString
        );
    };

    const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
            setShowCalendar(false);
        }
    };

    // Add event listener for clicking outside
    useEffect(() => {
        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendar]);

    // Format the current date for display
    const displayDate = selectedDate ? moment(selectedDate).format("l") : moment().format("l");
    // Below had timezone issue using moment()
    // const isSelectedToday = selectedDate ? moment(selectedDate).isSame(moment(), 'day') : isToday;
    // this fixes it
    const isSelectedToday = isToday;

    // Calculate defaultActiveStartDate dynamically
    const getDefaultActiveStartDate = () => {
        if (selectedDate) {
            return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        }
        if (minDateObj) {
            return new Date(minDateObj.getFullYear(), minDateObj.getMonth(), 1);
        }
        return new Date();
    };

    // Determine navigation label
    const getNavigationLabel = ({ date }) => {
        if (minDateObj && maxDateObj) {
            const minMonth = minDateObj.getMonth();
            const maxMonth = maxDateObj.getMonth();
            const minYear = minDateObj.getFullYear();
            const maxYear = maxDateObj.getFullYear();
            
            // If both dates are in the same month and year
            if (minMonth === maxMonth && minYear === maxYear) {
                return `${moment(minDateObj).format('MMMM YYYY')}`;
            }
        }
        return moment(date).format('MMMM YYYY');
    };

    // Determine if navigation should be disabled
    const shouldDisableNavigation = () => {
        if (minDateObj && maxDateObj) {
            const minMonth = minDateObj.getMonth();
            const maxMonth = maxDateObj.getMonth();
            const minYear = minDateObj.getFullYear();
            const maxYear = maxDateObj.getFullYear();
            
            // Disable navigation if all dates are in the same month
            return minMonth === maxMonth && minYear === maxYear;
        }
        return false;
    };

    return (
        <div className="between" style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <img
                    style={{ 
                        marginRight: "24px",
                        transform: 'scale(1.1)',
                        borderRadius: "12px"
                    }}
                    width={80}
                    height={80}
                    src={require(`../../clients/${locationData.logoPath}`)}
                    alt="logo"
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="my-auto" style={{ fontSize: "2.5rem", lineHeight: '1.2' }}>
                        {locationData.name}
                    </span>
                    {/* Display subName if it exists */}
                    {locationData.subName && (
                        <span className="my-auto" style={{ fontSize: "1.5rem", color: '#e20074', marginTop: '4px' }}>
                            {locationData.subName}
                        </span>
                    )}
                </div>
            </div>

            <div className="my-auto">
                <InputGroup>
                    {isSelectedToday && (
                        <div className="d-flex align-items-center">
                            <span style={{ color: "red", marginRight: "12px", fontSize: "1.25rem" }}>
                                LIVE
                            </span>
                            <span className="pulsing-dot" style={{ marginRight: "16px" }}></span>
                        </div>
                    )}
                    <ButtonGroup className="me-1">
                        <Button
                            className="panel-2 btn"
                            icon="calendar"
                            text={
                                displayDate +
                                (isSelectedToday ? " (Today)" : "")
                            }
                            onClick={handleCalendarToggle}
                            disabled={loading}
                        />
                        
                        <Button
                            disabled={true}
                            icon={"calendar-event"}
                            text="Event Day"
                            className={`panel-2 active`}
                        />
                    </ButtonGroup>
                    <ButtonGroup>
                        {isSelectedToday && (
                            <Button
                                className="border-0"
                                onClick={onRefresh}
                                disabled={loading}>
                                {!loading && (
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faArrowRotateRight} />
                                        <span style={{ marginLeft: "8px" }}>Refresh</span>
                                    </div>
                                )}
                            </Button>
                        )}
                    </ButtonGroup>
                </InputGroup>
            </div>

            {showCalendar && (
                <div
                    ref={calendarRef}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        right: '5%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        animation: 'fadeIn 0.2s ease-in-out',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        background: 'white',
                        borderRadius: '8px',
                    }}
                >
                    <div className="calendar-container">
                        <Calendar
                            onChange={handleDateSelect}
                            value={selectedDate}
                            minDate={minDateObj}
                            maxDate={maxDateObj}
                            defaultActiveStartDate={getDefaultActiveStartDate()}
                            minDetail="month"
                            maxDetail="month"
                            showNeighboringMonth={false}
                            navigationLabel={getNavigationLabel}
                            prev2Label={null}
                            next2Label={null}
                            prevLabel={shouldDisableNavigation() ? null : undefined}
                            nextLabel={shouldDisableNavigation() ? null : undefined}
                            tileDisabled={tileDisabled}
                        />
                    </div>
                </div>
            )}

            <style>
                {`
                    .pulsing-dot {
                        width: 12px;
                        height: 12px;
                        background-color: red;
                        border-radius: 50%;
                        animation: pulse 2s infinite;
                    }
                    
                    @keyframes pulse {
                        0% {
                            transform: scale(0.95);
                            box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7);
                        }
                        70% {
                            transform: scale(1);
                            box-shadow: 0 0 0 0 6px rgba(255, 0, 0, 0);
                        }
                        100% {
                            transform: scale(0.95);
                            box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
                        }
                    }

                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
                        to { opacity: 1; transform: translateX(-50%) translateY(0); }
                    }

                    .react-calendar__tile--active {
                        background: #53b8aa !important;
                        color: black !important;
                    }
                    .react-calendar__tile--active:enabled:hover,
                    .react-calendar__tile--active:enabled:focus {
                        background: #53b8aa !important;
                    }
                    .react-calendar__tile--now {
                        background: #9fdbd2 !important;
                    }
                    .react-calendar__tile--now:enabled:hover,
                    .react-calendar__tile--now:enabled:focus {
                        background: #9fdbd2 !important;
                    }

                    /* Disabled tiles styling */
                    .react-calendar__tile:disabled {
                        background-color: #f0f0f0 !important;
                        color: #ccc !important;
                        cursor: not-allowed !important;
                    }

                    /* Hide navigation arrows conditionally */
                    .react-calendar__navigation button {
                        display: ${shouldDisableNavigation() ? 'none' : 'flex'};
                    }
                    
                    /* Keep the month/year label visible but make it non-clickable */
                    .react-calendar__navigation__label {
                        display: flex !important;
                        pointer-events: none;
                        background: transparent !important;
                        font-weight: bold;
                    }
                `}
            </style>
        </div>
    );
};

export default EventHeaderNav;